import * as z from 'zod'
import { Location, Taxipark } from '~/shared/api'
import {
  dateSchema,
  uuidOptionSchema,
  promoCodeDetailsSchema,
} from '~/shared/lib/schemas'
import { FuelTypeEnum, TransmissionEnum } from '../config/enums'
import { ApiModel, ToOneRelation } from './core'

const attributesSchema = z.object({
  manufactureYear: z.string(),
  color: z.string(),
  fuelType: z.nativeEnum(FuelTypeEnum),
  brand: z.string(),
  model: z.string(),
  plateNumber: z.string(),
  pricePerDay: z.number(),
  transmission: z.nativeEnum(TransmissionEnum),
  vehicleCategory: z.string(),
  photos: z.array(z.string()),
  isLeasingFullyPaid: z.boolean().optional(),
  mileage: z.number(),
  cancellationFeeRub: z.number().nullable(),
  cancellationFeePercentage: z.number(),
  inspectionId: z.string().optional().nullable(),
  digitalbox: z.boolean().optional(),
  discounts: z
    .array(
      z.object({
        id: z.string(),
        details: promoCodeDetailsSchema,
        title: z.string().optional(),
        description: z.string().optional(),
      }),
    )
    .optional(),
})

const relationsSchema = z.object({
  locationId: uuidOptionSchema,
})

const schema = z
  .object({
    createdAt: dateSchema.optional(),
    updatedAt: dateSchema.optional(),
  })
  .merge(attributesSchema)
  .merge(relationsSchema)

export type BookingCarAttributes = z.infer<typeof attributesSchema>
export type BookingCarDiscount = NonNullable<
  BookingCarAttributes['discounts']
>[0]

export class BookingCar extends ApiModel<typeof schema, BookingCarAttributes> {
  static jsonApiType = 'booking-cars'

  static schema = schema

  getModel(): string {
    return this.getAttribute('model')
  }
  getBrand(): string {
    return this.getAttribute('brand')
  }
  getPlateNumber(): string {
    return this.getAttribute('plateNumber')
  }
  getPricePerDay(): number {
    return this.getAttribute('pricePerDay')
  }
  getPhotos(): string[] {
    return this.getAttribute('photos')
  }
  getIsLeasingFullyPaid(): boolean {
    return this.getAttribute('isLeasingFullyPaid')
  }
  getCancellationFeeRub(): number | null {
    return this.getAttribute('cancellationFeeRub')
  }
  getDigitalBox(): boolean {
    return this.getAttribute('digitalbox')
  }
  getInspectionId(): UniqueId {
    return this.getAttribute('inspectionId')
  }
  getDiscounts(): BookingCarDiscount[] | undefined {
    return this.getAttribute('discounts')
  }

  location(): ToOneRelation<Location, this> {
    return this.hasOne(Location)
  }
  getLocation(): Location {
    return this.getRelation('location')
  }

  taxipark(): ToOneRelation<Taxipark, this> {
    return this.hasOne(Taxipark)
  }
  getTaxipark(): Taxipark {
    return this.getRelation('taxipark')
  }
  // Используем первую скидку из массива по договоренности с беком
  getDiscount() {
    return this.getAttribute('discounts')[0]
  }

  static async getPrices({
    regionCode,
    locationIds,
    taxiparkId,
  }: {
    regionCode?: string
    locationIds?: string[]
    taxiparkId?: UniqueId
  }): Promise<{ min: string | null; max: string | null }> {
    const locationsFilter = locationIds?.length
      ? `locationIds=${locationIds}`
      : ''
    const regionCodeFilter = regionCode ? `regionCode=${regionCode}` : ''
    const taxiparkIdFilter = taxiparkId ? `taxiparkId=${taxiparkId}` : ''

    const filters = [regionCodeFilter, locationsFilter, taxiparkIdFilter]
      .filter(Boolean)
      .join('&')

    const url = `${BookingCar.getJsonApiUrl()}/actions/prices?${filters}`
    const client = BookingCar.httpClient.getImplementingClient()
    const { data } = await client.get<{
      data: {
        minPrice: string | null
        maxPrice: string | null
      }
    }>(url)

    return {
      min: data?.data?.minPrice || null,
      max: data?.data?.maxPrice || null,
    }
  }

  static async getModels({
    regionCode,
    locationIds,
    taxiparkId,
  }: {
    regionCode?: string
    locationIds?: string[]
    taxiparkId?: UniqueId
  }) {
    const locationsFilter = locationIds?.length
      ? `locationIds=${locationIds}`
      : ''
    const regionCodeFilter = regionCode ? `regionCode=${regionCode}` : ''
    const taxiparkIdFilter = taxiparkId ? `taxiparkId=${taxiparkId}` : ''

    const filters = [regionCodeFilter, locationsFilter, taxiparkIdFilter]
      .filter(Boolean)
      .join('&')

    const url = `${BookingCar.getJsonApiUrl()}/actions/models?${filters}`
    const client = BookingCar.httpClient.getImplementingClient()
    const { data } = await client.get<{ data: Record<string, string[]> }>(url)

    return data?.data
  }
}
